import { Divider, Drawer, Group, SimpleGrid, Stack } from '@mantine/core';
import type { CeleryTask } from '@repo/api-codegen';
import { Icon, Text, Title } from '@repo/foundations';
import dayjs from 'dayjs';

interface TaskDetailDrawerProps {
	task: CeleryTask | null;
	onClose: () => void;
}

export function TaskDetailDrawer({ task, onClose }: TaskDetailDrawerProps) {
	if (!task) return null;

	const formatTimeStart = (timeStart: number) =>
		dayjs.unix(timeStart).format('YYYY-MM-DD HH:mm:ss');

	return (
		<Drawer
			opened={!!task}
			onClose={onClose}
			title={
				<Group spacing="xs">
					<Icon name="code" color="icon/primary/default" size="md" />
					<Title size="md" weight="bold" truncate>
						Task: {task.name}
					</Title>
				</Group>
			}
			position="right"
			size="xl"
		>
			<Stack spacing="xs">
				{/* Basic Info */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Basic Info
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Task Name
							</Title>
							<Text size="sm" truncate>
								{task.name}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Task ID
							</Title>
							<Text size="sm" truncate>
								{task.id}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Worker
							</Title>
							<Text size="sm" truncate>
								{task.worker_name}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Status
							</Title>
							<Text size="sm">{task.status}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Type
							</Title>
							<Text size="sm" truncate>
								{task.type}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Worker PID
							</Title>
							<Text size="sm">{task.worker_pid}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Acknowledged
							</Title>
							<Text size="sm">{task.acknowledged ? 'Yes' : 'No'}</Text>
						</Stack>
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Timing */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Timing
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Start Time
							</Title>
							<Text size="sm">
								{task.time_start ? formatTimeStart(task.time_start) : 'None'}
							</Text>
						</Stack>
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Task Args */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Task Args
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Positional Args
							</Title>
							<Text size="sm" style={{ wordBreak: 'break-all' }}>
								{task.args && task.args.length > 0
									? JSON.stringify(task.args, null, 2)
									: 'None'}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Keyword Args
							</Title>
							<Text size="sm" style={{ wordBreak: 'break-all' }}>
								{task.kwargs && Object.keys(task.kwargs).length > 0
									? JSON.stringify(task.kwargs, null, 2)
									: 'None'}
							</Text>
						</Stack>
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Delivery Info */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Delivery Info
					</Title>
					<SimpleGrid cols={2}>
						{Object.entries(task.delivery_info).map(([key, value]) => (
							<Stack key={key} spacing={0}>
								<Title size="sm" weight="bold">
									{key}
								</Title>
								<Text size="sm" style={{ wordBreak: 'break-all' }}>
									{typeof value === 'object'
										? JSON.stringify(value, null, 2)
										: String(value)}
								</Text>
							</Stack>
						))}
					</SimpleGrid>
				</Stack>
			</Stack>
		</Drawer>
	);
}
